import { useCallback, useState } from "react"
import { Linking, TextStyle, View, ViewStyle } from "react-native"

import { router } from "expo-router"

import { Button, Screen, Text } from "src/components/Ignite"
import { useAuth } from "src/context/AuthContext"
import { useAiProcessingNoticeAcceptance } from "src/hooks/useAiProcessingNoticeAcceptance"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"

const PRIVACY_POLICY_URL = "https://sidekickwellness.com/privacy.html"

export default function AcceptAiProcessingScreen() {
  const { themed } = useAppTheme()
  const { logout, clearAllTemporaryData, getUserEmail } = useAuth()
  const { acceptAiProcessingNotice, declineAiProcessingNotice } =
    useAiProcessingNoticeAcceptance(getUserEmail())
  const [isDeclining, setIsDeclining] = useState(false)

  const onAccept = useCallback(() => {
    acceptAiProcessingNotice()
    router.replace("/")
  }, [acceptAiProcessingNotice])

  const onDecline = useCallback(async () => {
    if (isDeclining) return
    setIsDeclining(true)
    try {
      declineAiProcessingNotice()
      await logout()
      clearAllTemporaryData()
      router.replace("/unauth/login")
    } finally {
      setIsDeclining(false)
    }
  }, [declineAiProcessingNotice, logout, clearAllTemporaryData, isDeclining])

  const onPrivacyPolicyPress = useCallback(() => {
    Linking.openURL(PRIVACY_POLICY_URL)
  }, [])

  return (
    <Screen
      preset="scroll"
      style={themed($screenContainer)}
      contentContainerStyle={themed($contentContainer)}
      safeAreaEdges={["top", "bottom"]}
    >
      <View style={themed($content)}>
        <Text preset="heading" text="AI Feature Permission" style={themed($title)} />

        <Text style={themed($text)}>
          To provide our advanced features, the messages and text you input are processed by our
          proprietary platform. To assist our systems in generating your responses, this data may be
          shared with our supplementary infrastructure providers, including Amazon Web Services
          (AWS) and OpenAI, as described in our{" "}
          <Text style={themed($link)} onPress={onPrivacyPolicyPress}>
            Privacy Policy
          </Text>
          . Your data will not be used to train their models.
        </Text>

        <Text style={themed($question)}>
          Do you agree to share your inputs with these providers?
        </Text>

        <View style={themed($buttonRow)}>
          <Button preset="sidekick" text="I Agree" style={themed($button)} onPress={onAccept} />
          <Button
            preset="sidekick"
            text={isDeclining ? "Logging out…" : "I Don't Agree"}
            style={themed($button)}
            onPress={onDecline}
            disabled={isDeclining}
          />
        </View>
      </View>
    </Screen>
  )
}

const $screenContainer: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $contentContainer: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  padding: spacing.lg,
})

const $content: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
})

const $title: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginBottom: spacing.xl,
  textAlign: "center",
})

const $text: ThemedStyle<TextStyle> = () => ({})

const $question: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginTop: spacing.sm,
})

const $link: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.tint,
  textDecorationLine: "underline",
})

const $buttonRow: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexDirection: "row",
  gap: spacing.md,
  marginTop: spacing.xl,
})

const $button: ThemedStyle<ViewStyle> = () => ({ flex: 1 })
